import { useState, type CSSProperties, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../api/useAuth";
import { ApiError } from "../api/client";
import mohLogo from "../assets/moh-logo.webp";

// Palette sampled from the sign-in design: one deep-teal blue drives the
// heading, focus rings and the primary action; JSI red marks required fields;
// a warm amber carries the copyright line.
const REQUIRED = "#E61C2F";
const COPYRIGHT = "#FF9639";

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path d="m3 3 18 18" strokeLinecap="round" />
      <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c6.4 0 10 7 10 7a18 18 0 0 1-3.4 4.3M6.6 6.6A17.7 17.7 0 0 0 2 12s3.4 7 10 7a10.9 10.9 0 0 0 4.2-.9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" strokeLinecap="round" />
    </svg>
  );
}

const decorativeStyle = {
  backgroundImage: "radial-gradient(#c3ccd6 1px, transparent 1.2px)",
  backgroundSize: "13px 13px",
  WebkitMaskImage: "radial-gradient(closest-side at 58% 34%, #000 0%, transparent 72%)",
  maskImage: "radial-gradient(closest-side at 58% 34%, #000 0%, transparent 72%)",
} as CSSProperties;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      // apiFetch doesn't read 401 bodies, and /auth/login only returns 401
      // for bad credentials. 403s carry the backend's reason ("Account
      // approval required", "Account OTP verification required", ...). The
      // login wrapper can also throw a plain Error (e.g. the cookie-session
      // guard) whose message explains what to fix — surface it verbatim.
      if (err instanceof ApiError && err.status === 401) {
        setError("Incorrect email or password");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Login failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-[14px] leading-5 text-gray-900 placeholder:text-gray-400 transition focus:border-[#0B5785] focus:outline-none focus:ring-2 focus:ring-[#0B5785]/20";

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#F9FAFB] text-gray-900">
      {/* Faint decorative field — a dotted grid and a wireframe globe in the
          top-right corner, as in the design. Purely ornamental. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-24 -top-16 h-[560px] w-[640px] opacity-[0.4]" style={decorativeStyle} />
        <svg
          className="absolute -right-16 -top-20 h-[360px] w-[360px] text-[#c3ccd6]"
          viewBox="0 0 200 200"
          fill="none"
          stroke="currentColor"
          strokeWidth={0.7}
          opacity={0.5}
        >
          <circle cx="100" cy="100" r="95" />
          <ellipse cx="100" cy="100" rx="32" ry="95" />
          <ellipse cx="100" cy="100" rx="64" ry="95" />
          <ellipse cx="100" cy="100" rx="95" ry="32" />
          <ellipse cx="100" cy="100" rx="95" ry="64" />
          <line x1="5" y1="100" x2="195" y2="100" />
          <line x1="100" y1="5" x2="100" y2="195" />
        </svg>
      </div>

      {/* Card */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-[380px] rounded-2xl border border-gray-100 bg-white px-9 pb-9 pt-10 shadow-[0_10px_40px_-8px_rgba(15,23,42,0.12)]">
          <img src={mohLogo} alt="Ministry of Health, Ethiopia" className="mx-auto h-auto w-[104px]" />

          <div className="mt-7 border-t border-gray-200" />

          <h1 className="mt-6 text-center text-[22px] font-bold leading-none text-[#0B5785] dark:text-[#79A9FF]">
            Sign In
          </h1>

          <form onSubmit={handleSubmit} className="mt-8">
            <label
              htmlFor="email"
              className="mb-1.5 block text-[12px] font-semibold uppercase leading-4 tracking-wide text-gray-500"
            >
              Email Address<span style={{ color: REQUIRED }}> *</span>
            </label>
            <input
              id="email"
              type="email"
              required
              autoFocus
              autoComplete="username"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />

            <label
              htmlFor="password"
              className="mb-1.5 mt-5 block text-[12px] font-semibold uppercase leading-4 tracking-wide text-gray-500"
            >
              Password<span style={{ color: REQUIRED }}> *</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center px-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            </div>

            {error && (
              <p className="mt-4 text-[13px] text-red-600" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-8 w-full rounded-lg bg-[#0B5785] py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0a4a71] focus:outline-none focus:ring-2 focus:ring-[#0B5785]/40 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>

      {/* Copyright */}
      <footer className="relative z-10 pb-5 text-center text-sm" style={{ color: COPYRIGHT }}>
        © Copyright EAII 2026 | All rights reserved
      </footer>
    </div>
  );
}
